import React, { ReactElement, FC, useState } from 'react'
import { Col, Row, Tag, Typography } from 'antd'
import {
  getAddress,
  getSpaceCapacity,
  getStatuses,
} from '../../../../../../Graphql-Utilities'
import {
  formatCents,
  createCompressedImgxImageUrl,
} from '../../../../../../Utilities'
import { ProposalModal } from '../../../../ProposalModal'
import {
  ProposalCardContentEventFragment,
  ProposalCardContentProposalFragment,
} from './ProposalCardContent.generated-types'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import { ProjectedTravelEmissionsIcon } from '../../../../../../Components/ProjectedTravelEmissionsIcon'
import Icon from '@ant-design/icons'

const IMAGE_HEIGHT: number = 180

const { Text } = Typography

const LowestTravelEmissionsSVG = (): JSX.Element => (
  <svg
    width="12"
    height="16"
    viewBox="0 0 12 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Group 116">
      <path
        id="Ellipse 13"
        d="M1.23645 8.28998C0.553485 10.647 1.91061 13.1115 4.26768 13.7944C6.62474 14.4774 9.08917 13.1203 9.77213 10.7632C10.4551 8.40614 9.6371 5.10277 7.62522 2.20675C5.17031 2.97473 1.91941 5.93292 1.23645 8.28998Z"
        fill="#BEDABE"
      />
      <path
        id="Subtract"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.26743 13.7937C6.6245 14.4767 9.08893 13.1196 9.77189 10.7625C10.4549 8.40545 9.63686 5.10208 7.62498 2.20606L4.26743 13.7937Z"
        fill="#E5F0E5"
      />
    </g>
  </svg>
)

const FavouriteIconSVG = (): JSX.Element => (
  <svg
    height="1em"
    viewBox="0 0 11 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.16776 8.93985L3.39136 10.0155V4.29395H7.60908V10.0405L5.67509 8.9336L5.41958 8.78737L5.16776 8.93985Z"
      fill="#D7918E"
      stroke="#D7918E"
    />
    <path
      d="M5.42673 3.79395H2.89136V10.9027L5.42673 9.36756V3.79395Z"
      fill="#8C1C16"
    />
    <circle cx="5.5" cy="3.78516" r="3.6875" fill="#8C1C16" />
  </svg>
)

interface ProposalCardContentProps {
  event: ProposalCardContentEventFragment
  proposal: ProposalCardContentProposalFragment
  totalEmissionsForAllJourneys: number | null
  displayLowestTravelEmissionsBanner: boolean
}

export const ProposalCardContent: FC<ProposalCardContentProps> = ({
  event,
  event: { currency },
  proposal,
  proposal: {
    baseCostTotal,
    space: { spaceName, spacePhotos, venue },
    hasPreferredVenue,
  },
  totalEmissionsForAllJourneys,
  displayLowestTravelEmissionsBanner,
}): ReactElement => {
  const { user } = useGlobalAuth()

  const [isProposalModalVisible, setIsProposalModalVisible] = useState(false)

  const address = getAddress(proposal)
  const spaceCapacity = getSpaceCapacity({ event, proposal })
  const canRequestToBook = getStatuses(event).isActiveWithResponses

  const handleCloseProposalModal = (): void => setIsProposalModalVisible(false)

  return (
    <>
      <ProposalModal
        modalVisible={isProposalModalVisible}
        handleCloseModal={handleCloseProposalModal}
        response={proposal}
        canRequestToBook={canRequestToBook}
        event={event}
      />
      <div
        style={{
          height: IMAGE_HEIGHT,
          width: '100%',
          display: 'flex',
          gap: '24px',
          overflow: 'hidden',
          border: `1px solid ${
            displayLowestTravelEmissionsBanner ? '#4F987B' : '#D9D9D9'
          }`,
          borderRadius: '4px',
        }}
      >
        <div style={{ flex: '40%' }}>
          {spacePhotos?.length > 0 && (
            <div
              style={{ position: 'relative', height: '100%', width: '100%' }}
            >
              <img
                src={createCompressedImgxImageUrl({
                  originalImgxImageUrl: spacePhotos[0].url,
                  height: IMAGE_HEIGHT,
                })}
                alt="Space photo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {displayLowestTravelEmissionsBanner ? (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    background: '#187759',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    paddingLeft: '12px',
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '18px',
                  }}
                >
                  <Icon component={LowestTravelEmissionsSVG} />
                  <Text style={{ color: '#FFFFFF', marginLeft: '4px' }}>
                    Lowest travel emissions
                  </Text>
                </div>
              ) : null}
            </div>
          )}
        </div>
        <div
          style={{
            flex: '60%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            paddingTop: '16px',
            paddingBottom: '16px',
            paddingRight: '16px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div>
                {/*
                 * TODO: remove this when venue is made non-nullable
                 * Venue should probably not be nullable but if it somehow is, it is better to show
                 * nothing than to crash the entire application
                 */}
                {venue?.name ? (
                  <Text strong style={{ fontSize: '18px' }}>
                    {venue.name}
                  </Text>
                ) : null}
                <Text> </Text>
                <Text>{spaceName}</Text>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '18px',
                  marginTop: '4px',
                }}
              >
                {hasPreferredVenue ? (
                  <>
                    <Tag
                      style={{
                        fontSize: '10px',
                        marginRight: 'unset',
                        lineHeight: 'inherit',
                        backgroundColor: '#F7E9E8',
                      }}
                    >
                      <Icon component={FavouriteIconSVG} />
                      <Text strong style={{ fontSize: 'inherit' }}>{`${
                        user?.companyName != null
                          ? user.companyName
                          : "My company's"
                      } favourite`}</Text>
                    </Tag>
                    <Text style={{ marginLeft: '4px', fontSize: '12px' }}>
                      Bookers in your company love this venue
                    </Text>
                  </>
                ) : null}
              </div>
            </div>
            <div>
              <Text strong style={{ fontSize: '18px' }}>
                {formatCents(currency, baseCostTotal)}
              </Text>
            </div>
          </div>
          <Row>
            <Col span={16}>
              <div>
                <Text style={{ fontSize: '12px' }}>Location</Text>
              </div>
              {/*
               * TODO: remove this when venue is made non-nullable
               * Venue should probably not be nullable but if it somehow is, it is better to show
               * nothing than to crash the entire application
               */}
              {address || venue?.nearestTransit ? (
                <div>
                  <Text strong style={{ fontSize: '18px' }}>
                    {
                      // If there is no nearest transit information, as a fallback show the venue's address instead
                    }
                    {venue?.nearestTransit?.station &&
                    venue.nearestTransit.distanceKm
                      ? `${
                          venue.nearestTransit.station
                        } (${venue.nearestTransit.distanceKm.toFixed(1)}km)`
                      : address}
                  </Text>
                </div>
              ) : null}
            </Col>
            <Col span={8}>
              <div>
                <div>
                  <Text style={{ fontSize: '12px' }}>Capacity</Text>
                </div>
                <div>
                  <Text strong style={{ fontSize: '18px' }}>
                    {spaceCapacity}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
          {totalEmissionsForAllJourneys ? (
            <div style={{ marginTop: '12px' }}>
              <Col span={24}>
                <div style={{ display: 'inline-flex' }}>
                  <div style={{ display: 'inline-block' }}>
                    <ProjectedTravelEmissionsIcon />
                  </div>
                  <Text
                    style={{
                      marginLeft: '4px',
                      fontSize: '14px',
                    }}
                  >
                    <Text>Projected travel emissions for this venue are</Text>
                    <Text> </Text>
                    <Text strong>{`${totalEmissionsForAllJourneys.toFixed(
                      2
                    )}kg`}</Text>
                    <Text> </Text>
                    <Text>of CO2e</Text>
                  </Text>
                </div>
              </Col>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
